import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import './StageReplayModal.css';

const SPEED_OPTIONS = [1, 5, 20, 60];

// Индексы полей в сэмпле телеметрии: [t, distance, altitude, speed, power, heart_rate]
const S_TIME = 0;
const S_DISTANCE = 1;
const S_SPEED = 3;
const S_POWER = 4;
const S_HR = 5;

const formatClock = (seconds) => {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;

  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
};

// Значения участника на момент времени t с линейной интерполяцией между сэмплами
const stateAt = (samples, time) => {
  if (!samples || samples.length === 0) return null;

  const last = samples[samples.length - 1];

  if (time >= last[S_TIME]) {
    return {
      distance: last[S_DISTANCE],
      speed: 0,
      power: null,
      heartRate: last[S_HR],
      finished: true
    };
  }

  // Бинарный поиск отрезка, в который попадает время
  let low = 0;
  let high = samples.length - 1;

  while (high - low > 1) {
    const mid = (low + high) >> 1;
    if (samples[mid][S_TIME] <= time) {
      low = mid;
    } else {
      high = mid;
    }
  }

  const a = samples[low];
  const b = samples[high];
  const span = b[S_TIME] - a[S_TIME];
  const k = span > 0 ? (time - a[S_TIME]) / span : 0;
  const lerp = (index) => {
    if (a[index] === null || a[index] === undefined) return b[index];
    if (b[index] === null || b[index] === undefined) return a[index];
    return a[index] + (b[index] - a[index]) * k;
  };

  return {
    // Дистанцию интерполируем - от нее зависит плавность движения по профилю
    distance: lerp(S_DISTANCE),
    // Пульс, скорость и мощность берем как значение текущего окна телеметрии:
    // цифры меняются раз в 5 секунд, а не дергаются каждый кадр
    speed: a[S_SPEED],
    power: a[S_POWER],
    heartRate: a[S_HR],
    finished: false
  };
};

// Момент времени, когда участник проехал заданную дистанцию.
// Нужен для расчета отставания по времени: сколько прошло с тех пор,
// как лидер был в той же точке маршрута
const timeAtDistance = (samples, distance) => {
  if (!samples || samples.length === 0) return null;
  if (distance <= samples[0][S_DISTANCE]) return samples[0][S_TIME];

  const last = samples[samples.length - 1];
  if (distance >= last[S_DISTANCE]) return last[S_TIME];

  let low = 0;
  let high = samples.length - 1;

  while (high - low > 1) {
    const mid = (low + high) >> 1;
    if (samples[mid][S_DISTANCE] <= distance) {
      low = mid;
    } else {
      high = mid;
    }
  }

  const a = samples[low];
  const b = samples[high];
  const span = b[S_DISTANCE] - a[S_DISTANCE];
  const k = span > 0 ? (distance - a[S_DISTANCE]) / span : 0;

  return a[S_TIME] + (b[S_TIME] - a[S_TIME]) * k;
};

// Плавная линия через точки: сегменты соединяются квадратичными кривыми
const traceSmoothPath = (ctx, points) => {
  if (points.length === 0) return;

  ctx.lineTo(points[0][0], points[0][1]);

  for (let i = 1; i < points.length - 1; i += 1) {
    const midX = (points[i][0] + points[i + 1][0]) / 2;
    const midY = (points[i][1] + points[i + 1][1]) / 2;
    ctx.quadraticCurveTo(points[i][0], points[i][1], midX, midY);
  }

  const last = points[points.length - 1];
  ctx.lineTo(last[0], last[1]);
};

const StageReplayModal = ({ isOpen, onClose, eventId, routeId, routeName }) => {
  const { t, language } = useLanguage();
  // Цвета холста берутся из токенов темы — при переключении нужна перерисовка
  const { theme } = useTheme();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  // Счетчик ручных перезагрузок - кнопка "Повторить" при ошибке
  const [reloadToken, setReloadToken] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(5);
  const [selectedRiderId, setSelectedRiderId] = useState(null);
  // Скрытые участники: по умолчанию показываем всех проехавших этап
  const [hiddenRiderIds, setHiddenRiderIds] = useState(() => new Set());
  const [ridersPickerOpen, setRidersPickerOpen] = useState(false);
  // Масштаб графика и центр видимого участка (в метрах дистанции)
  const [zoom, setZoom] = useState(1);
  const [viewCenter, setViewCenter] = useState(null);
  // Автослежение: график держит в центре выбранного участника, а если никто
  // не выбран - лидера заезда, чтобы никто не уезжал за край при увеличении
  const [autoFollow, setAutoFollow] = useState(true);

  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const lastFrameRef = useRef(null);
  // Экранные позиции участников для попадания по клику
  const hitAreasRef = useRef([]);
  // Геометрия последней отрисовки - нужна для зума колесом и перетаскивания
  const geometryRef = useRef(null);
  const dragRef = useRef(null);

  // Профиль сглаживаем скользящим средним - высота в FIT шумит на метровом уровне
  const profile = useMemo(() => {
    const raw = (data && data.profile) || [];
    if (raw.length < 5) return raw;

    const window = 4;

    return raw.map((point, index) => {
      let sum = 0;
      let count = 0;

      for (let i = Math.max(0, index - window); i <= Math.min(raw.length - 1, index + window); i += 1) {
        sum += raw[i][1];
        count += 1;
      }

      return [point[0], sum / count];
    });
  }, [data]);

  // Границы высот считаем один раз по всему маршруту, чтобы масштаб по вертикали
  // не менялся при зуме и прокрутке
  const altitudeRange = useMemo(() => {
    if (profile.length === 0) return { min: 0, max: 1 };

    const altitudes = profile.map(point => point[1]);

    return { min: Math.min(...altitudes), max: Math.max(...altitudes) };
  }, [profile]);

  const riderName = useCallback((rider) => (
    language !== 'ru' && rider.first_name_eng && rider.last_name_eng
      ? `${rider.first_name_eng} ${rider.last_name_eng}`
      : `${rider.first_name} ${rider.last_name}`
  ), [language]);

  // Загрузка данных реплея
  useEffect(() => {
    if (!isOpen || !eventId || !routeId) return undefined;

    let cancelled = false;

    const loadReplay = async () => {
      setLoading(true);
      setError(null);
      setData(null);
      setCurrentTime(0);
      setPlaying(false);
      setSelectedRiderId(null);
      setHiddenRiderIds(new Set());
      setRidersPickerOpen(false);
      setZoom(1);
      setViewCenter(null);
      setAutoFollow(true);

      try {
        const response = await axios.get(`/api/events/${eventId}/results/replay/${routeId}`);
        if (!cancelled) setData(response.data);
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.error || t('results.replay_error'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadReplay();

    return () => {
      cancelled = true;
      // Иначе после закрытия окна во время запроса остается вечная "Загрузка..."
      setLoading(false);
    };
    // t намеренно не в зависимостях: функция перевода пересоздается при каждом
    // рендере провайдера и сбрасывала бы уже загруженные данные
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, eventId, routeId, reloadToken]);

  // Проигрывание
  useEffect(() => {
    if (!playing || !data) return undefined;

    const step = (timestamp) => {
      if (lastFrameRef.current === null) lastFrameRef.current = timestamp;
      const delta = (timestamp - lastFrameRef.current) / 1000;
      lastFrameRef.current = timestamp;

      setCurrentTime(prev => {
        const next = prev + delta * speed;
        if (next >= data.duration) {
          setPlaying(false);
          return data.duration;
        }
        return next;
      });

      animationRef.current = requestAnimationFrame(step);
    };

    animationRef.current = requestAnimationFrame(step);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      lastFrameRef.current = null;
    };
  }, [playing, speed, data]);

  // Отрисовка сцены
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data) return;

    const ctx = canvas.getContext('2d');
    // Холст не подхватывает CSS-переменные сам — берем значения токенов темы вручную
    const canvasStyles = getComputedStyle(canvas);
    const themeColor = (token, fallback) => canvasStyles.getPropertyValue(token).trim() || fallback;
    const profileLineColor = themeColor('--c-4c5aa8-fg', '#4c5aa8');
    const tickColor = themeColor('--c-d7dbe6-bd', '#d7dbe6');
    const riderLabelColor = themeColor('--c-2d3748-fg', '#2d3748');

    const ratio = window.devicePixelRatio || 1;
    const cssWidth = canvas.clientWidth;
    const cssHeight = canvas.clientHeight;

    if (canvas.width !== cssWidth * ratio || canvas.height !== cssHeight * ratio) {
      canvas.width = cssWidth * ratio;
      canvas.height = cssHeight * ratio;
    }

    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, cssWidth, cssHeight);

    const totalDistance = data.total_distance || (profile.length ? profile[profile.length - 1][0] : 1);

    const padLeft = 14;
    const padRight = 14;
    const padTop = 22;
    const padBottom = 30;
    const plotWidth = cssWidth - padLeft - padRight;
    const plotHeight = cssHeight - padTop - padBottom;

    // Видимый участок дистанции с учетом масштаба
    const viewSpan = totalDistance / zoom;
    let center = viewCenter;

    if (zoom > 1 && autoFollow) {
      const visible = (data.riders || []).filter(rider => !hiddenRiderIds.has(rider.user_id));

      // Ведем выбранного участника, а если он не выбран - лидера заезда
      let followed = selectedRiderId
        ? visible.find(rider => rider.user_id === selectedRiderId)
        : null;

      if (!followed) {
        let bestDistance = -Infinity;

        visible.forEach(rider => {
          const state = stateAt(rider.samples, currentTime);
          if (state && state.distance > bestDistance) {
            bestDistance = state.distance;
            followed = rider;
          }
        });
      }

      const followedState = followed ? stateAt(followed.samples, currentTime) : null;
      if (followedState) center = followedState.distance;
    }

    if (center === null || center === undefined) center = totalDistance / 2;

    const viewStart = Math.min(
      Math.max(center - viewSpan / 2, 0),
      Math.max(0, totalDistance - viewSpan)
    );
    const viewEnd = viewStart + viewSpan;

    // Вертикальный масштаб фиксирован по всему маршруту и не зависит от зума и сдвига
    const minAlt = altitudeRange.min;
    const maxAlt = altitudeRange.max;
    const altSpan = Math.max(1, maxAlt - minAlt);

    const xFor = (distance) => padLeft + ((distance - viewStart) / viewSpan) * plotWidth;
    const yFor = (altitude) => padTop + plotHeight - ((altitude - minAlt) / altSpan) * plotHeight * 0.78;

    geometryRef.current = { padLeft, plotWidth, viewStart, viewSpan, totalDistance };

    // Высота маршрута на заданной дистанции
    const altAt = (distance) => {
      if (profile.length === 0) return minAlt;
      if (distance <= profile[0][0]) return profile[0][1];
      if (distance >= profile[profile.length - 1][0]) return profile[profile.length - 1][1];

      let low = 0;
      let high = profile.length - 1;
      while (high - low > 1) {
        const mid = (low + high) >> 1;
        if (profile[mid][0] <= distance) low = mid; else high = mid;
      }
      const span = profile[high][0] - profile[low][0];
      const k = span > 0 ? (distance - profile[low][0]) / span : 0;
      return profile[low][1] + (profile[high][1] - profile[low][1]) * k;
    };

    if (profile.length > 1) {
      // Все, что вне видимого участка, обрезаем
      ctx.save();
      ctx.beginPath();
      ctx.rect(padLeft, 0, plotWidth, cssHeight);
      ctx.clip();

      // Точки профиля в экранных координатах - рисуем сглаженной кривой
      const screenPoints = profile.map(([d, alt]) => [xFor(d), yFor(alt)]);

      // Заливка профиля высот
      const faceGradient = ctx.createLinearGradient(0, padTop, 0, padTop + plotHeight);
      faceGradient.addColorStop(0, 'rgba(102, 126, 234, 0.55)');
      faceGradient.addColorStop(1, 'rgba(102, 126, 234, 0.12)');
      ctx.beginPath();
      ctx.moveTo(screenPoints[0][0], padTop + plotHeight);
      traceSmoothPath(ctx, screenPoints);
      ctx.lineTo(screenPoints[screenPoints.length - 1][0], padTop + plotHeight);
      ctx.closePath();
      ctx.fillStyle = faceGradient;
      ctx.fill();

      ctx.beginPath();
      ctx.moveTo(screenPoints[0][0], screenPoints[0][1]);
      traceSmoothPath(ctx, screenPoints);
      ctx.strokeStyle = profileLineColor;
      ctx.lineWidth = 2;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.stroke();

      ctx.restore();
    }

    // Сетка высот: минимум, середина и максимум профиля
    if (profile.length > 1) {
      ctx.font = '11px sans-serif';
      ctx.textAlign = 'right';
      [minAlt, (minAlt + maxAlt) / 2, maxAlt].forEach(altitude => {
        const y = yFor(altitude);
        ctx.beginPath();
        ctx.moveTo(padLeft, y);
        ctx.lineTo(padLeft + plotWidth, y);
        ctx.strokeStyle = 'rgba(120, 132, 170, 0.18)';
        ctx.lineWidth = 1;
        ctx.stroke();

        // Подпись не рисуем у самого низа - там километровая шкала
        if (y < padTop + plotHeight - 14) {
          ctx.fillStyle = '#9aa3b8';
          ctx.fillText(`${Math.round(altitude)} m`, padLeft + plotWidth - 2, y - 4);
        }
      });
    }

    // Километровая шкала - шаг подбираем под видимый участок
    const kmStep = viewSpan > 40000
      ? 10000
      : (viewSpan > 15000 ? 5000 : (viewSpan > 4000 ? 1000 : (viewSpan > 1500 ? 500 : 100)));
    ctx.font = '11px sans-serif';
    ctx.fillStyle = '#9aa3b8';
    ctx.textAlign = 'center';
    const firstTick = Math.ceil(viewStart / kmStep) * kmStep;
    for (let d = firstTick; d <= viewEnd; d += kmStep) {
      const x = xFor(d);
      ctx.beginPath();
      ctx.moveTo(x, padTop + plotHeight);
      ctx.lineTo(x, padTop + plotHeight + 4);
      ctx.strokeStyle = tickColor;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillText(`${Math.round(d / 1000)}`, x, padTop + plotHeight + 17);
    }
    ctx.fillText('km', padLeft + plotWidth + 12, padTop + plotHeight + 17);

    // Участники - только выбранные для отображения
    const riders = (data.riders || [])
      .filter(rider => !hiddenRiderIds.has(rider.user_id))
      .map(rider => ({
        rider,
        state: stateAt(rider.samples, currentTime)
      }))
      .filter(item => item.state);

    const sorted = [...riders].sort((a, b) => b.state.distance - a.state.distance);
    const leaderId = sorted.length ? sorted[0].rider.user_id : null;

    const hitAreas = [];

    // Дальние участники рисуются первыми, чтобы лидер был сверху
    [...riders].sort((a, b) => a.state.distance - b.state.distance).forEach(({ rider, state }) => {
      // За пределами видимого участка не рисуем
      if (state.distance < viewStart || state.distance > viewEnd) return;

      const isSelected = rider.user_id === selectedRiderId;
      const isLeader = rider.user_id === leaderId;
      // Участник стоит на линии профиля
      const markerX = xFor(state.distance);
      const markerY = yFor(altAt(state.distance));
      const radius = isSelected ? 8 : 5.5;

      ctx.beginPath();
      ctx.arc(markerX, markerY - radius, radius, 0, Math.PI * 2);
      ctx.fillStyle = isSelected ? '#e8590c' : (state.finished ? '#94a3b8' : '#3b82f6');
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = isLeader ? '#f4b400' : '#ffffff';
      ctx.stroke();

      if (isSelected || isLeader) {
        ctx.font = '600 11px sans-serif';
        ctx.fillStyle = riderLabelColor;
        ctx.textAlign = 'center';
        ctx.fillText(rider.nickname || riderName(rider), markerX, markerY - radius * 2 - 6);
      }

      hitAreas.push({ userId: rider.user_id, x: markerX, y: markerY - radius, r: radius + 8 });
    });

    hitAreasRef.current = hitAreas;
  }, [
    data,
    profile,
    altitudeRange,
    currentTime,
    selectedRiderId,
    hiddenRiderIds,
    zoom,
    viewCenter,
    autoFollow,
    riderName,
    theme
  ]);

  // Перерисовка при изменении размеров окна
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleResize = () => setCurrentTime(prev => prev);
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  // Масштабирование колесом мыши с привязкой к точке под курсором
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data) return undefined;

    const handleWheel = (event) => {
      event.preventDefault();

      const geometry = geometryRef.current;
      if (!geometry) return;

      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const ratio = Math.min(Math.max((x - geometry.padLeft) / geometry.plotWidth, 0), 1);
      const anchorDistance = geometry.viewStart + ratio * geometry.viewSpan;

      const factor = event.deltaY < 0 ? 1.25 : 1 / 1.25;
      const nextZoom = Math.min(Math.max(zoom * factor, 1), 40);
      const nextSpan = geometry.totalDistance / nextZoom;

      setZoom(nextZoom);
      // Зум колесом не выключает слежение - иначе участники снова уедут за край
      setViewCenter(nextZoom === 1 ? null : anchorDistance - (ratio - 0.5) * nextSpan);
    };

    canvas.addEventListener('wheel', handleWheel, { passive: false });

    return () => canvas.removeEventListener('wheel', handleWheel);
  }, [data, zoom]);

  const handlePointerDown = (event) => {
    if (zoom <= 1) return;
    dragRef.current = { x: event.clientX, moved: false };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    const geometry = geometryRef.current;
    if (!drag || !geometry) return;

    const deltaX = event.clientX - drag.x;
    if (Math.abs(deltaX) < 1) return;

    drag.x = event.clientX;
    drag.moved = true;

    const deltaDistance = -(deltaX / geometry.plotWidth) * geometry.viewSpan;
    // Ручное перетаскивание временно отключает слежение
    setAutoFollow(false);
    setViewCenter(prev => {
      const current = prev === null || prev === undefined
        ? geometry.viewStart + geometry.viewSpan / 2
        : prev;
      return current + deltaDistance;
    });
  };

  const handlePointerUp = (event) => {
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    // Клик после перетаскивания не должен выбирать участника
    setTimeout(() => { dragRef.current = null; }, 0);
  };

  const changeZoom = (factor) => {
    const geometry = geometryRef.current;
    const nextZoom = Math.min(Math.max(zoom * factor, 1), 40);

    setZoom(nextZoom);

    if (nextZoom === 1) {
      setViewCenter(null);
    } else if (geometry && (viewCenter === null || viewCenter === undefined)) {
      // Увеличиваем от текущего центра видимого участка
      setViewCenter(geometry.viewStart + geometry.viewSpan / 2);
    }
  };

  const resetZoom = () => {
    setZoom(1);
    setViewCenter(null);
    setAutoFollow(true);
  };

  const handleCanvasClick = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (dragRef.current && dragRef.current.moved) return;

    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    let closest = null;
    let closestDistance = Infinity;

    hitAreasRef.current.forEach(area => {
      const dx = area.x - x;
      const dy = area.y - y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance <= area.r && distance < closestDistance) {
        closest = area.userId;
        closestDistance = distance;
      }
    });

    setSelectedRiderId(closest);
    // Выбор участника всегда возвращает слежение
    setAutoFollow(true);
  };

  const toggleRider = (userId) => {
    setHiddenRiderIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
        // Скрытого участника снимаем с выделения
        setSelectedRiderId(current => (current === userId ? null : current));
      }
      return next;
    });
  };

  const showAllRiders = () => setHiddenRiderIds(new Set());

  const hideAllRiders = () => {
    setHiddenRiderIds(new Set((data?.riders || []).map(rider => rider.user_id)));
    setSelectedRiderId(null);
  };

  if (!isOpen) return null;

  const riders = data ? data.riders || [] : [];
  // Список всех проехавших этап - для выбора отображаемых, по месту на этапе
  const allRiders = [...riders].sort((a, b) => {
    if (a.position && b.position) return a.position - b.position;
    if (a.position) return -1;
    if (b.position) return 1;
    return (a.finish_time || 0) - (b.finish_time || 0);
  });
  const visibleRiders = riders.filter(rider => !hiddenRiderIds.has(rider.user_id));

  const rawStandings = visibleRiders
    .map(rider => ({ rider, state: stateAt(rider.samples, currentTime) }))
    .filter(item => item.state)
    .sort((a, b) => b.state.distance - a.state.distance);

  // Отставание от лидера: по дистанции - разница метров,
  // по времени - сколько прошло с момента, когда лидер был в этой же точке
  const leader = rawStandings.length ? rawStandings[0] : null;
  const standings = rawStandings.map(item => {
    if (!leader || item.rider.user_id === leader.rider.user_id) {
      return { ...item, gapDistance: 0, gapTime: 0 };
    }

    const gapDistance = leader.state.distance - item.state.distance;
    const leaderTimeHere = timeAtDistance(leader.rider.samples, item.state.distance);
    const gapTime = leaderTimeHere === null ? null : Math.max(0, currentTime - leaderTimeHere);

    return { ...item, gapDistance, gapTime };
  });

  const selected = standings.find(item => item.rider.user_id === selectedRiderId);

  const hasGap = (item) => Boolean(item) && item.gapDistance > 0.5;

  const gapTimeText = (item) => {
    if (!hasGap(item) || item.gapTime === null || item.gapTime === undefined) return null;

    return item.gapTime >= 60
      ? `+${formatClock(item.gapTime)}`
      : `+${item.gapTime.toFixed(1)} ${t('results.replay_seconds')}`;
  };

  const gapDistanceText = (item) => {
    if (!hasGap(item)) return null;

    return `+${Math.round(item.gapDistance)} ${t('results.replay_meters')}`;
  };

  const formatGap = (item) => {
    const timePart = gapTimeText(item);
    const distancePart = gapDistanceText(item);

    if (!distancePart) return null;

    return timePart ? `${timePart} · ${distancePart}` : distancePart;
  };

  return (
    <div className="stage-replay-overlay" onClick={onClose}>
      <div className="stage-replay-modal" onClick={(e) => e.stopPropagation()}>
        <div className="stage-replay-header">
          <div>
            <h3 className="stage-replay-title">{t('results.replay_title')}</h3>
            <div className="stage-replay-subtitle">{routeName}</div>
          </div>
          <button className="stage-replay-close" onClick={onClose} aria-label={t('common.close')}>
            &times;
          </button>
        </div>

        <div className="stage-replay-body">
          {loading && <div className="stage-replay-message">{t('common.loading')}</div>}
          {error && !loading && (
            <div className="stage-replay-message">
              <div>{error}</div>
              <button
                className="stage-replay-retry"
                onClick={() => setReloadToken(prev => prev + 1)}
              >
                {t('results.replay_retry')}
              </button>
            </div>
          )}

          {data && !loading && (
            <>
              <div className="stage-replay-riders-picker">
                <button
                  className="stage-replay-riders-toggle"
                  onClick={() => setRidersPickerOpen(prev => !prev)}
                >
                  {t('results.replay_riders')}: {visibleRiders.length}/{riders.length}
                  <span className={`stage-replay-riders-chevron ${ridersPickerOpen ? 'open' : ''}`}>▾</span>
                </button>

                {ridersPickerOpen && (
                  <div className="stage-replay-riders-list">
                    <div className="stage-replay-riders-actions">
                      <button onClick={showAllRiders}>{t('results.replay_select_all')}</button>
                      <button onClick={hideAllRiders}>{t('results.replay_clear_all')}</button>
                    </div>
                    {allRiders.map(rider => (
                      <label key={rider.user_id} className="stage-replay-rider-option">
                        <input
                          type="checkbox"
                          checked={!hiddenRiderIds.has(rider.user_id)}
                          onChange={() => toggleRider(rider.user_id)}
                        />
                        <span className="stage-replay-rider-option-place">
                          {rider.position || '-'}
                        </span>
                        <span className="stage-replay-rider-option-name">
                          {riderName(rider)}
                          {rider.nickname ? <em> @{rider.nickname}</em> : null}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="stage-replay-canvas-wrap">
                <canvas
                  ref={canvasRef}
                  className={`stage-replay-canvas ${zoom > 1 ? 'zoomed' : ''}`}
                  onClick={handleCanvasClick}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerCancel={handlePointerUp}
                />

                <div className="stage-replay-zoom">
                  <button onClick={() => changeZoom(1 / 1.5)} title={t('results.replay_zoom_out')}>−</button>
                  <span className="stage-replay-zoom-value">{zoom.toFixed(1)}x</span>
                  <button onClick={() => changeZoom(1.5)} title={t('results.replay_zoom_in')}>+</button>
                  <button
                    className="stage-replay-zoom-reset"
                    onClick={resetZoom}
                    title={t('results.replay_zoom_reset')}
                  >
                    ⤢
                  </button>
                </div>
              </div>

              {zoom > 1 && (
                <div className="stage-replay-zoom-hint">
                  {autoFollow ? (
                    selectedRiderId
                      ? t('results.replay_zoom_following')
                      : t('results.replay_zoom_following_leader')
                  ) : (
                    <>
                      {t('results.replay_zoom_drag')}
                      <button
                        className="stage-replay-follow-resume"
                        onClick={() => setAutoFollow(true)}
                      >
                        {t('results.replay_zoom_follow_resume')}
                      </button>
                    </>
                  )}
                </div>
              )}

              <div className="stage-replay-controls">
                <button
                  className="stage-replay-play"
                  onClick={() => {
                    // При старте воспроизведения слежение включаем обратно
                    setPlaying(prev => {
                      if (!prev) setAutoFollow(true);
                      return !prev;
                    });
                  }}
                >
                  {playing ? '❚❚' : '▶'}
                </button>

                <input
                  type="range"
                  className="stage-replay-slider"
                  min="0"
                  max={data.duration}
                  step="1"
                  value={Math.round(currentTime)}
                  onChange={(e) => {
                    setPlaying(false);
                    setCurrentTime(Number(e.target.value));
                  }}
                />

                <div className="stage-replay-clock">
                  {formatClock(currentTime)} / {formatClock(data.duration)}
                </div>

                <div className="stage-replay-speeds">
                  {SPEED_OPTIONS.map(option => (
                    <button
                      key={option}
                      className={`stage-replay-speed ${speed === option ? 'active' : ''}`}
                      onClick={() => setSpeed(option)}
                    >
                      {option}x
                    </button>
                  ))}
                </div>
              </div>

              {selected ? (
                <div className="stage-replay-rider-card">
                  <div className="stage-replay-rider-head">
                    <div>
                      <div className="stage-replay-rider-name">{riderName(selected.rider)}</div>
                      <div className="stage-replay-rider-meta">
                        @{selected.rider.nickname}
                        {selected.rider.team_name ? ` · ${selected.rider.team_name}` : ''}
                      </div>
                      <div className="stage-replay-rider-gap">
                        {formatGap(selected)
                          ? `${t('results.replay_gap')}: ${formatGap(selected)}`
                          : t('results.replay_gap_leader')}
                      </div>
                    </div>
                    <button
                      className="stage-replay-rider-clear"
                      onClick={() => setSelectedRiderId(null)}
                    >
                      &times;
                    </button>
                  </div>
                  <div className="stage-replay-metrics">
                    <div className="stage-replay-metric">
                      <span>{t('results.replay_heart_rate')}</span>
                      <strong>{selected.state.heartRate ? Math.round(selected.state.heartRate) : '—'}</strong>
                      <em>bpm</em>
                    </div>
                    <div className="stage-replay-metric">
                      <span>{t('results.replay_speed')}</span>
                      {/* В FIT скорость в м/с - переводим в км/ч */}
                      <strong>
                        {selected.state.speed === null || selected.state.speed === undefined
                          ? '—'
                          : (selected.state.speed * 3.6).toFixed(1)}
                      </strong>
                      <em>km/h</em>
                    </div>
                    <div className="stage-replay-metric">
                      <span>{t('results.replay_power')}</span>
                      <strong>{selected.state.power ? Math.round(selected.state.power) : '—'}</strong>
                      <em>W</em>
                    </div>
                    <div className="stage-replay-metric">
                      <span>{t('results.replay_distance')}</span>
                      <strong>{(selected.state.distance / 1000).toFixed(2)}</strong>
                      <em>km</em>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="stage-replay-hint">{t('results.replay_hint')}</div>
              )}

              <div className="stage-replay-standings">
                {standings.length === 0 && (
                  <div className="stage-replay-standings-empty">{t('results.replay_no_riders')}</div>
                )}
                {standings.map((item, index) => (
                  <button
                    key={item.rider.user_id}
                    className={`stage-replay-standing ${item.rider.user_id === selectedRiderId ? 'active' : ''}`}
                    onClick={() => setSelectedRiderId(item.rider.user_id)}
                  >
                    <span className="stage-replay-standing-place">{index + 1}</span>
                    <span className="stage-replay-standing-name">
                      {riderName(item.rider)}
                      {item.state.finished && <span className="stage-replay-standing-finished"> ✔</span>}
                    </span>
                    {/* Колонки фиксированной ширины - цифры не скачут по строкам */}
                    <span className="stage-replay-standing-distance">
                      {(item.state.distance / 1000).toFixed(2)} km
                    </span>
                    <span className="stage-replay-standing-gap">
                      {gapTimeText(item) || '—'}
                    </span>
                    <span className="stage-replay-standing-gap">
                      {gapDistanceText(item) || '—'}
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StageReplayModal;
